import { ReactNode } from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { cn } from '../../lib/utils'

export interface MarkdownBodyImageProps {
  src?: string
  alt?: string
}

export interface MarkdownBodyLinkProps {
  href?: string
  children?: ReactNode
}

export interface MarkdownBodyProps {
  content: string
  className?: string
  /**
   * `compact` flattens every heading to the same small bold sans size so a
   * dense review surface (workflow gates) can show more body without a
   * display-face title stack.
   * `thread` keeps default body size and flattens every heading to that
   * same `text-base` sans semibold — thread artifact cards, not reports.
   */
  density?: 'default' | 'compact' | 'thread'
  /**
   * Optional image override. Return `undefined` to keep the default bounded
   * `<img>`. App-specific media (CreativeMedia) belongs in the caller, not here.
   */
  renderImage?: (props: MarkdownBodyImageProps) => ReactNode
  /**
   * Optional link override. Return `undefined` to keep today's `target=_blank`
   * anchor. Citation chips and `doc:` provenance chips belong in the caller.
   */
  renderLink?: (props: MarkdownBodyLinkProps) => ReactNode
  urlTransform?: (url: string) => string
}

// Static markdown only: workflow detail pages, the skills editor preview, and
// report bodies. There is no streaming reveal and no citation graph here —
// `ThreadAnswerBody` (apps/frontend/src/pages/ThreadDetailPage) owns that job
// for live assistant answers. Keep the two renderers separate rather than
// growing this one a `citations` prop later; a static body has nothing to
// anchor markers to.
//
// Injection props (`renderImage`, `renderLink`, `urlTransform`) are the seam
// so app-specific CreativeMedia / citation chips stay out of design-system.
// This is not a citations prop — do not import apps/frontend into this file.
//
// Headings use `font-display` (the serif heading face), unlike
// `ThreadAnswerBody`, which opts back out to `font-sans` because a streamed
// answer piles up several headings per screen and the display face reads as
// noise at that density. A workflow page or a report has one heading run at a
// time, so it is meant to read like page furniture.
//
// The serif is reserved for h1 alone: one page title reads as furniture, but
// the same face on every subheading turns it into noise. Default h1 is
// `text-xl` (same size as h2) and stays `font-medium` on purpose — the serif
// already carries enough visual weight that bolding it on top would be
// redundant. h2-h4 render in the sans body face instead, with `font-semibold`
// doing the weight work the serif no longer provides.
const HEADING_BASE_H1 = 'font-display font-medium text-foreground first:mt-0'
const HEADING_BASE_REST = 'font-sans font-semibold text-foreground first:mt-0'
// Same weight as `strong` below: a compact gate card treats `##` and `**` as
// the same "regular bold," not a display-style heading vs. an inline emphasis.
// Exported so `MarkdownHighlightTextarea` (the gate's editor overlay) can
// match this exactly rather than keeping its own copy of the class string.
export const MARKDOWN_REGULAR_BOLD_CLASS = 'font-semibold text-foreground'
const HEADING_COMPACT = cn('font-sans text-xs leading-snug first:mt-0', MARKDOWN_REGULAR_BOLD_CLASS)
const HEADING_THREAD = cn(
  'font-sans text-base leading-[1.7] first:mt-0',
  MARKDOWN_REGULAR_BOLD_CLASS,
)

// A fenced block carries `language-*` from remark; inline code carries
// nothing. Only the inline case gets the pill — the block case is styled by
// `pre`, which also neutralises any pill a language-less fence would
// otherwise pick up.
function codeClassName(className?: string): string {
  if (className) {
    return className
  }
  return 'rounded bg-muted px-1 py-0.5 text-[0.9em]'
}

export function MarkdownBody(props: MarkdownBodyProps) {
  const {
    content,
    className,
    density = 'default',
    renderImage,
    renderLink,
    urlTransform = defaultUrlTransform,
  } = props

  const isCompact = density === 'compact'
  const isThread = density === 'thread'
  const headingBaseH1 = isCompact
    ? HEADING_COMPACT
    : isThread
      ? HEADING_THREAD
      : HEADING_BASE_H1
  const headingBaseRest = isCompact
    ? HEADING_COMPACT
    : isThread
      ? HEADING_THREAD
      : HEADING_BASE_REST

  const isEmpty = content.trim().length === 0

  // Guard rather than let `ReactMarkdown` mount on nothing: a report or
  // workflow step with no body yet should render an empty, testable
  // container, not a stray empty paragraph.
  const markdown = isEmpty ? null : (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      urlTransform={urlTransform}
      components={{
        h1: ({ children }) => (
          <h1
            className={cn(
              isCompact
                ? 'mb-1 mt-2'
                : isThread
                  ? 'mb-1 mt-4'
                  : 'mb-2 mt-10 text-xl leading-tight',
              headingBaseH1,
            )}
          >
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2
            className={cn(
              isCompact
                ? 'mb-1 mt-2'
                : isThread
                  ? 'mb-1 mt-4'
                  : 'mb-2 mt-10 text-xl leading-snug',
              headingBaseRest,
            )}
          >
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3
            className={cn(
              isCompact
                ? 'mb-1 mt-2'
                : isThread
                  ? 'mb-1 mt-4'
                  : 'mb-1 mt-9 text-lg leading-snug',
              headingBaseRest,
            )}
          >
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4
            className={cn(
              isCompact
                ? 'mb-1 mt-2'
                : isThread
                  ? 'mb-1 mt-4'
                  : 'mb-1 mt-6 text-base leading-snug',
              headingBaseRest,
            )}
          >
            {children}
          </h4>
        ),
        p: ({ children }) => (
          <p className={cn(isCompact ? 'my-2' : 'my-4', 'first:mt-0 last:mb-0')}>
            {children}
          </p>
        ),
        strong: ({ children }) => (
          <strong className={MARKDOWN_REGULAR_BOLD_CLASS}>{children}</strong>
        ),
        ul: ({ children }) => (
          <ul
            className={cn(
              isCompact ? 'my-2' : 'my-4',
              'ml-5 list-disc space-y-1 first:mt-0 last:mb-0',
            )}
          >
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol
            className={cn(
              isCompact ? 'my-2' : 'my-4',
              'ml-5 list-decimal space-y-1 first:mt-0 last:mb-0',
            )}
          >
            {children}
          </ol>
        ),
        li: ({ children }) => <li className="my-1 leading-[1.7]">{children}</li>,
        hr: () => <hr className="my-10 border-t border-border/40" />,
        blockquote: ({ children }) => (
          <blockquote className="my-6 border-l-2 border-border/60 pl-4 italic text-muted-foreground">
            {children}
          </blockquote>
        ),
        table: ({ children }) => (
          <div className="my-6 overflow-x-auto" data-testid="markdown-body-table-scroll">
            <table className="w-full border-collapse text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="border-b border-border">{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr className="border-b border-border/30 last:border-0">{children}</tr>,
        th: ({ children }) => <th className="px-3 py-2 text-left font-medium">{children}</th>,
        td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
        code: ({ className: codeClass, children }) => (
          <code className={codeClassName(codeClass)}>{children}</code>
        ),
        pre: ({ children }) => (
          <pre className="my-6 overflow-x-auto rounded-md border border-border bg-muted/50 p-4 text-[0.9em] [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit">
            {children}
          </pre>
        ),
        a: ({ href, children }) => {
          const custom = renderLink?.({ href, children })
          if (custom !== undefined) {
            return custom
          }
          return (
            <a
              href={href}
              className="text-primary underline underline-offset-2"
              target="_blank"
              rel="noreferrer"
            >
              {children}
            </a>
          )
        },
        img: ({ src, alt }) => {
          const custom = renderImage?.({ src, alt })
          if (custom !== undefined) {
            return custom
          }
          return (
            <img
              src={src}
              alt={alt ?? ''}
              className="max-w-full h-auto"
              data-testid="markdown-body-image"
            />
          )
        },
      }}
    >
      {content}
    </ReactMarkdown>
  )

  return (
    <div
      className={cn(
        isCompact
          ? 'max-w-none text-xs leading-relaxed text-foreground'
          : 'max-w-prose text-base leading-[1.7] text-foreground',
        className,
      )}
      data-testid="markdown-body"
      data-density={density}
    >
      {markdown}
    </div>
  )
}
